import { gsap } from 'gsap';

export interface PlantDetailPayload {
  title: string;
  subtitle: string;
  description: string;
  care: string[];
  imageUrl: string;
}

const SCAN_DURATION = 1.25;

/** Default plant shown when opening from "Identify a Plant" (uses updated hero background image). */
const ADONIS_VERNALIS: PlantDetailPayload = {
  title: 'Adonis vernalis',
  subtitle: "Pheasant's eye / Spring adonis",
  description:
    'A perennial flowering plant with bright yellow, buttercup-like flowers and feathery foliage. Native to dry grasslands and steppes of Europe and western Asia. Blooms in spring; contains cardiac glycosides and is used in traditional medicine but is toxic if ingested.',
  care: [
    'Full sun; well-draining, neutral to alkaline soil. Tolerates dry conditions once established.',
    'Water moderately in spring and early summer; avoid wet winters. Good drainage is essential.',
    'Avoid disturbing roots; slow to establish. Best left undisturbed; suitable for rock gardens and dry borders.',
  ],
  imageUrl: '/hero_bg.jpg',
};

export class PlantScanDialog {
  private root: HTMLElement | null = null;
  private backdrop: HTMLElement | null = null;
  private card: HTMLElement | null = null;
  private scannerPhase: HTMLElement | null = null;
  private scanLine: HTMLElement | null = null;
  private progressFill: HTMLElement | null = null;
  private progressPct: HTMLElement | null = null;
  private resultPhase: HTMLElement | null = null;
  private titleEl: HTMLElement | null = null;
  private subtitleEl: HTMLElement | null = null;
  private descriptionEl: HTMLElement | null = null;
  private careList: HTMLElement | null = null;
  private closeBtn: HTMLButtonElement | null = null;
  private imageEl: HTMLImageElement | null = null;
  private scanTimeline: gsap.core.Timeline | null = null;
  private boundClose: () => void;
  private boundBackdropClick: (e: MouseEvent) => void;
  private boundKeydown: (e: KeyboardEvent) => void;
  private previousActiveElement: HTMLElement | null = null;

  constructor() {
    this.boundClose = this.close.bind(this);
    this.boundBackdropClick = this.handleBackdropClick.bind(this);
    this.boundKeydown = this.handleKeydown.bind(this);

    this.root = document.getElementById('plant-scan-dialog');
    if (!this.root) return;

    this.backdrop = this.root.querySelector('.plant-scan-dialog-backdrop');
    this.card = this.root.querySelector('.plant-scan-dialog-card');
    this.scannerPhase = this.root.querySelector('.plant-scan-dialog-scanner-phase');
    this.scanLine = this.root.querySelector('.plant-scan-dialog-scan-line');
    this.progressFill = this.root.querySelector('.plant-scan-dialog-progress-fill');
    this.progressPct = this.root.querySelector('.plant-scan-dialog-progress-pct');
    this.resultPhase = this.root.querySelector('.plant-scan-dialog-result-phase');
    this.titleEl = this.root.querySelector('.plant-scan-dialog-title');
    this.subtitleEl = this.root.querySelector('.plant-scan-dialog-subtitle');
    this.descriptionEl = this.root.querySelector('.plant-scan-dialog-description');
    this.careList = this.root.querySelector('.plant-scan-dialog-care');
    this.closeBtn = this.root.querySelector('.plant-scan-dialog-close');
    this.imageEl = this.root.querySelector('.plant-scan-dialog-image');

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this.boundClose);
    }
    if (this.backdrop) {
      this.backdrop.addEventListener('click', this.boundBackdropClick);
    }
  }

  public open(payload?: PlantDetailPayload): void {
    if (!this.root || !this.scannerPhase || !this.resultPhase) return;

    this.previousActiveElement =
      (document.activeElement as HTMLElement) || document.getElementById('hero-cta-identify');

    const data = payload ?? ADONIS_VERNALIS;
    this.populateContent(data);

    this.root.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', this.boundKeydown);

    this.scannerPhase.classList.remove('plant-scan-dialog-scanner-hidden');
    this.resultPhase.setAttribute('aria-hidden', 'true');
    this.resultPhase.classList.remove('plant-scan-dialog-result-visible');

    gsap.set(this.card, { scale: 0.94, opacity: 0 });
    gsap.set(this.scannerPhase, { opacity: 1 });
    gsap.set(this.scanLine, { top: 0 });
    gsap.set(this.progressFill, { width: '0%' });
    if (this.progressPct) this.progressPct.textContent = '0%';

    this.scanTimeline?.kill();
    this.scanTimeline = gsap.timeline({
      onComplete: () => {
        this.closeBtn?.focus();
        this.scanTimeline = null;
      },
    });

    this.scanTimeline
      .to(this.card, {
        scale: 1,
        opacity: 1,
        duration: 0.35,
        ease: 'back.out(1.2)',
        overwrite: true,
      })
      .to(this.scanLine, {
        top: '100%',
        duration: SCAN_DURATION,
        ease: 'power2.inOut',
        overwrite: true,
      })
      .to(
        this.progressFill,
        {
          width: '100%',
          duration: SCAN_DURATION,
          ease: 'power2.inOut',
          overwrite: true,
        },
        '<'
      )
      .add(() => {
        if (this.progressPct) this.progressPct.textContent = '100%';
      }, SCAN_DURATION - 0.05)
      .to(this.scannerPhase, {
        opacity: 0,
        duration: 0.25,
        ease: 'power2.in',
      })
      .add(() => {
        this.scannerPhase?.classList.add('plant-scan-dialog-scanner-hidden');
        this.resultPhase?.setAttribute('aria-hidden', 'false');
        this.resultPhase?.classList.add('plant-scan-dialog-result-visible');
        gsap.set(this.resultPhase, { opacity: 0, visibility: 'visible' });
      })
      .to(this.resultPhase, {
        opacity: 1,
        duration: 0.35,
        ease: 'power2.out',
      });
  }

  public close(): void {
    if (!this.root) return;

    this.scanTimeline?.kill();
    this.scanTimeline = null;

    this.root.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', this.boundKeydown);

    if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
      this.previousActiveElement.focus();
    }
    this.previousActiveElement = null;
  }

  private populateContent(data: PlantDetailPayload): void {
    if (this.titleEl) this.titleEl.textContent = data.title;
    if (this.subtitleEl) this.subtitleEl.textContent = data.subtitle;
    if (this.descriptionEl) this.descriptionEl.textContent = data.description;
    if (this.careList) {
      this.careList.innerHTML = data.care.map((tip) => `<li>${tip}</li>`).join('');
    }
    if (this.imageEl && data.imageUrl) {
      this.imageEl.src = data.imageUrl;
      this.imageEl.alt = data.title;
    }
  }

  private handleBackdropClick(e: MouseEvent): void {
    if (e.target === this.backdrop) {
      this.close();
    }
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.close();
    }
  }

  public dispose(): void {
    this.scanTimeline?.kill();
    this.scanTimeline = null;
    this.closeBtn?.removeEventListener('click', this.boundClose);
    this.backdrop?.removeEventListener('click', this.boundBackdropClick);
    document.removeEventListener('keydown', this.boundKeydown);
    this.root = null;
    this.backdrop = null;
    this.card = null;
    this.scannerPhase = null;
    this.scanLine = null;
    this.progressFill = null;
    this.progressPct = null;
    this.resultPhase = null;
    this.titleEl = null;
    this.subtitleEl = null;
    this.descriptionEl = null;
    this.careList = null;
    this.closeBtn = null;
    this.imageEl = null;
    this.previousActiveElement = null;
  }
}
